import type { VisitStatus, ConsentState, ConnectionStatus } from '@assistmd/types';
import { cn } from '../../lib/design-tokens';

export type BadgeKind = 'visit' | 'consent' | 'connection' | 'severity';

interface StatusBadgeProps {
  /** The status value */
  status: string;
  /** Kind of badge — determines color mapping */
  kind?: BadgeKind;
  /** Optional size */
  size?: 'sm' | 'md';
  /** Enable pulse animation for active statuses (recording, granted, connected) */
  pulse?: boolean;
}

/** Dot color + subtle background tint per status */
interface StatusStyle {
  dot: string;
  bg: string;
  text: string;
  ring: string;
}

const visitStyles: Record<VisitStatus, StatusStyle> = {
  draft:      { dot: 'bg-slate-400',  bg: 'bg-slate-50',     text: 'text-slate-600',  ring: 'ring-slate-200' },
  recording:  { dot: 'bg-red-500',    bg: 'bg-red-50',       text: 'text-red-700',    ring: 'ring-red-200' },
  finalizing: { dot: 'bg-amber-500',  bg: 'bg-amber-50',     text: 'text-amber-700',  ring: 'ring-amber-200' },
  closed:     { dot: 'bg-slate-400',  bg: 'bg-slate-50',     text: 'text-slate-600',  ring: 'ring-slate-200' },
};

const consentStyles: Record<ConsentState, StatusStyle> = {
  unknown: { dot: 'bg-slate-400',   bg: 'bg-slate-50',    text: 'text-slate-600',  ring: 'ring-slate-200' },
  granted: { dot: 'bg-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-700', ring: 'ring-emerald-200' },
  revoked: { dot: 'bg-red-500',     bg: 'bg-red-50',      text: 'text-red-700',    ring: 'ring-red-200' },
  expired: { dot: 'bg-amber-700',   bg: 'bg-amber-50',    text: 'text-amber-700',  ring: 'ring-amber-200' },
};

const connectionStyles: Record<ConnectionStatus, StatusStyle> = {
  connecting:   { dot: 'bg-amber-500',   bg: 'bg-amber-50',    text: 'text-amber-700',   ring: 'ring-amber-200' },
  connected:    { dot: 'bg-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-700', ring: 'ring-emerald-200' },
  disconnected: { dot: 'bg-red-500',     bg: 'bg-red-50',      text: 'text-red-700',     ring: 'ring-red-200' },
};

const severityStyles: Record<string, StatusStyle> = {
  critical: { dot: 'bg-red-500',   bg: 'bg-red-50',    text: 'text-red-700',    ring: 'ring-red-200' },
  warning:  { dot: 'bg-amber-500', bg: 'bg-amber-50',  text: 'text-amber-700',  ring: 'ring-amber-200' },
  info:     { dot: 'bg-cyan-500',  bg: 'bg-cyan-50',   text: 'text-cyan-700',   ring: 'ring-cyan-200' },
};

const fallbackStyle: StatusStyle = {
  dot: 'bg-slate-500',
  bg: 'bg-slate-50',
  text: 'text-slate-600',
  ring: 'ring-slate-200',
};

/** Statuses that pulse by default when pulse prop is not explicitly set */
const pulseStatuses = new Set(['recording', 'granted', 'connected']);

function getStyle(status: string, kind: BadgeKind): StatusStyle {
  switch (kind) {
    case 'visit':
      return visitStyles[status as VisitStatus] ?? fallbackStyle;
    case 'consent':
      return consentStyles[status as ConsentState] ?? fallbackStyle;
    case 'connection':
      return connectionStyles[status as ConnectionStatus] ?? fallbackStyle;
    case 'severity':
      return severityStyles[status] ?? fallbackStyle;
    default:
      return fallbackStyle;
  }
}

function formatLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
}

/**
 * Status indicator with colored dot, tinted background, ring border, and optional pulse.
 * Premium pill shape with ring-1 border treatment and per-status color mapping.
 */
export function StatusBadge({
  status,
  kind = 'visit',
  size = 'sm',
  pulse,
}: StatusBadgeProps): JSX.Element {
  const style = getStyle(status, kind);
  const shouldPulse = pulse ?? pulseStatuses.has(status);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full ring-1 ring-inset',
        style.bg,
        style.ring,
        size === 'sm' ? 'px-2.5 py-0.5 text-xs' : 'px-3 py-1 text-sm',
      )}
    >
      <span
        className={cn(
          'status-dot h-1.5 w-1.5',
          style.dot,
          shouldPulse && 'animate-pulse-glow',
        )}
      />
      <span className={cn('font-medium tracking-tight', style.text)}>
        {formatLabel(status)}
      </span>
    </span>
  );
}
